import type { DocumentDto } from "../../../services/documentService";

/**
 * EIN BILD, DAS IN DER NOTIZ STEHT, IST KEINE ZWEITE DOKUMENTKARTE
 * (User 23.10.2026).
 *
 * ══ WOHER DIE DOPPELTE KARTE KOMMT ═════════════════════════════════════════
 * Ein Bild IN der Notiz ist ein eigenes Dokument mit `category='Notizbild'`;
 * der Server hält diese Kategorie aus jeder Dokumentliste heraus
 * (`document.service.ts`, PO 14.09.2026). Das Notizbild selbst wird also nie
 * zur Karte.
 *
 * Die Karte daneben ist ein ZWEITER Upload derselben Datei als gewöhnliches
 * Dokument: Bis #2265 lag beim Ziehen eines Bilds das Lila „Dateien an …
 * hochladen" über dem Notizfeld; der erste Drop landete dort als Dokument am
 * To-do, das Lila blieb stehen, und der Nutzer zog das Bild ein zweites Mal —
 * diesmal ins Feld. Auf DEV: Dokument 106 (am To-do 267, ohne Kategorie) und
 * die Notizbilder 107/108 in den Notizen 96/97, alle mit DEMSELBEN
 * `contentHash`. Dasselbe passiert, wer ein Bild bewusst erst an die Karte
 * und dann in die Notiz legt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DER BEZUG IST DER INHALT, NICHT DER NAME ══════════════════════════════
 * Ausgeblendet wird ein Dokument, dessen `contentHash` (SHA-256 der Bytes,
 * beim Upload gesetzt) gleich dem eines Notizbilds ist, das in einer
 * aktiven Notiz dieses Vorgangs als `<img data-bild-id>` STEHT. Ein
 * gleichnamiger Screenshot mit anderem Inhalt bleibt Karte; ein aus der
 * Notiz entferntes Bild gibt seine Karte wieder frei.
 *
 * Nur im Verlauf des Vorgangs. In der Kundenakte bleibt das Dokument stehen:
 * dort gibt es keine Notiz, die es zeigt — es wäre sonst nirgends mehr zu
 * finden. Ein kommentiertes Dokument bleibt ebenfalls Karte, sonst wäre sein
 * Faden unerreichbar.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const BILD_MARKER = /<img\b[^>]*\bdata-bild-id\s*=\s*["']?(\d+)/gi;

/** Die Ids aller Notizbilder, die in diesen Notiztexten stehen. */
export function bildIdsInTexten(texte: Iterable<string | null | undefined>): Set<number> {
    const ids = new Set<number>();
    for (const text of texte) {
        if (!text || !text.includes("data-bild-id")) continue;
        for (const m of text.matchAll(BILD_MARKER)) {
            const id = Number(m[1]);
            if (Number.isFinite(id) && id > 0) ids.add(id);
        }
    }
    return ids;
}

/**
 * Die Dokumente ohne die, deren Inhalt schon als Bild in einer Notiz steht.
 *
 * @param dokumente  die Karten-Kandidaten des Verlaufs
 * @param notizbilder die Notizbild-Dokumente des Vorgangs (mit `contentHash`)
 * @param texte      die Texte der aktiven Notizen und Kommentare
 * @param verankert  Dokument-Ids mit Kommentar — die bleiben immer stehen
 */
export function ohneNotizbildDoppel(
    dokumente: readonly DocumentDto[],
    notizbilder: readonly DocumentDto[],
    texte: Iterable<string | null | undefined>,
    verankert: ReadonlySet<number> = new Set(),
): DocumentDto[] {
    if (!notizbilder.length || !dokumente.length) return [...dokumente];
    const eingebettet = bildIdsInTexten(texte);
    if (!eingebettet.size) return [...dokumente];
    const hashes = new Set<string>();
    for (const b of notizbilder) {
        if (eingebettet.has(b.id) && b.contentHash) hashes.add(b.contentHash);
    }
    if (!hashes.size) return [...dokumente];
    return dokumente.filter(
        (d) => verankert.has(d.id) || !d.contentHash || !hashes.has(d.contentHash),
    );
}
